/**
 * Functions for applying WebsiteUpdate changes to WebsiteData.
 * Extracted from WebsiteUpdaterContext for reusability and testability.
 */
import { uid } from 'uid';

import { SessionChangeContent } from '@superfunnel/types/agent-session';
import { CMSFilterClause } from '@superfunnel/types/content-library';
import { PopupData } from '@superfunnel/types/popup';
import {
  SectionData,
  accountSectionTypeToVariantMapping,
  sectionTypeToVariantMapping,
} from '@superfunnel/types/section';
import { WebsiteData } from '@superfunnel/types/website';
import { WebsiteActionElementData } from '@superfunnel/types/website-preview-event';
import {
  applySessionChangeContent,
  MergeConflicts,
  mergeValue,
} from '@superfunnel/utils/agent-changes';

import { mergeImages } from '../../components/FormFields/FileUpload/FileUploadInput';
import { extractUsedSearchParamsFromSection } from '../../utils/quill';
import { WebsiteUpdate } from '../WebsiteUpdaterContext';
import { revertTouched } from '../../utils/revert-touched';
import { mergeElementEdit } from './merge-element-edit';

import {
  applyDeleteHtmlListItem,
  applyDuplicateHtmlListItem,
  applyMoveHtmlListItem,
  applyToggleHtmlListItemHidden,
} from '@/utils/html-list-utils';
import {
  applyAssetSettingsToHtml,
  applyHtmlElementChangeToHtml,
  applySelectAssetToHtml,
  applySelectThumbnailToHtml,
  getHtmlFromDoc,
  parseHtmlAndFindElement,
  parseHtmlFragment,
} from '@/utils/html-element-utils';

const CUSTOM_EVENT_DATA_ATTRIBUTE = 'data-sf-custom-events';
const CUSTOM_EVENT_ATTRIBUTE_PREFIX = 'data-event-';

// ============================================================================
// HTML Action Helpers (Website-specific)
// ============================================================================

/**
 * Apply HTML action changes (link, form) to an action element.
 * Handles tag conversion (button <-> anchor) when needed.
 *
 * For landing pages: action is 'link' | 'form' | 'none'
 */
function applyHtmlActionToHtml(
  html: string,
  elementPath: string,
  content: WebsiteActionElementData,
): { success: boolean; html?: string; error?: string } {
  const result = parseHtmlAndFindElement(html, elementPath);
  if (!result) {
    return { success: false, error: `Element not found: ${elementPath}` };
  }

  const { doc, element } = result;

  const { url, action, isConversion, openInNewTab, formId, popupId } = content;

  const currentTagName = element.tagName.toLowerCase();
  let finalElement: Element = element;

  // Determine if this is a link action
  const isLinkAction = action === 'link';

  if (isLinkAction) {
    // Convert button to anchor for links
    if (currentTagName === 'button') {
      const newAnchor = doc.createElement('a');
      Array.from(element.attributes).forEach((attr) => {
        newAnchor.setAttribute(attr.name, attr.value);
      });
      newAnchor.innerHTML = element.innerHTML;
      element.parentNode?.replaceChild(newAnchor, element);
      finalElement = newAnchor;
    }

    // Set link attributes
    finalElement.setAttribute('href', url || '#');
    finalElement.setAttribute('target', openInNewTab ? '_blank' : '_self');
    finalElement.setAttribute('data-action-type', 'link');
    finalElement.removeAttribute('data-form-id');
    finalElement.removeAttribute('data-popup-id');
  } else {
    // Non-link actions (form, popup, none)

    // Convert anchor to button for non-link actions
    if (currentTagName === 'a') {
      const newButton = doc.createElement('button');
      Array.from(element.attributes).forEach((attr) => {
        newButton.setAttribute(attr.name, attr.value);
      });
      newButton.innerHTML = element.innerHTML;
      if (!newButton.hasAttribute('type')) {
        newButton.setAttribute('type', 'button');
      }
      element.parentNode?.replaceChild(newButton, element);
      finalElement = newButton;
    }

    // Set action type
    if (action) {
      finalElement.setAttribute('data-action-type', action);
    } else {
      finalElement.removeAttribute('data-action-type');
    }

    // Handle form action
    if (action === 'form' && formId) {
      finalElement.setAttribute('data-form-id', formId);
    } else {
      finalElement.removeAttribute('data-form-id');
    }

    // Handle popup action
    if (action === 'popup' && popupId) {
      finalElement.setAttribute('data-popup-id', popupId);
    } else {
      finalElement.removeAttribute('data-popup-id');
    }

    // Remove link-specific attributes
    finalElement.removeAttribute('href');
    finalElement.removeAttribute('target');
  }

  // Set conversion tracking
  if (isConversion) {
    finalElement.setAttribute('data-conversion', 'true');
  } else {
    finalElement.removeAttribute('data-conversion');
  }

  // Handle custom event attributes.
  // First, remove any existing custom event attributes.
  const existingEventAttrs: string[] = [];
  for (const attr of Array.from(finalElement.attributes)) {
    if (
      attr.name === CUSTOM_EVENT_DATA_ATTRIBUTE ||
      attr.name.startsWith(CUSTOM_EVENT_ATTRIBUTE_PREFIX)
    ) {
      existingEventAttrs.push(attr.name);
    }
  }
  for (const attrName of existingEventAttrs) {
    finalElement.removeAttribute(attrName);
  }

  // Then, add new custom event attributes from content
  const customEventData = (content as any).customEventData as
    Record<string, string> | undefined;
  if (customEventData) {
    const customEventEntries: Record<string, string> = {};

    for (const [key, value] of Object.entries(customEventData)) {
      if (key && value != null) {
        customEventEntries[key] = String(value);
        finalElement.setAttribute(
          `${CUSTOM_EVENT_ATTRIBUTE_PREFIX}${key}`,
          String(value),
        );
      }
    }

    if (Object.keys(customEventEntries).length) {
      finalElement.setAttribute(
        CUSTOM_EVENT_DATA_ATTRIBUTE,
        JSON.stringify(customEventEntries),
      );
    }
  }

  return { success: true, html: getHtmlFromDoc(doc) };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Find a section by ID in either sections or banners array.
 */
export function findSection(
  development: WebsiteData,
  sectionId: string,
): SectionData | undefined {
  return (
    development.sections?.find((s: any) => s.id === sectionId) ??
    development.banners?.find((s: any) => s.id === sectionId)
  );
}

/**
 * Find a popup by ID in the popups array.
 */
export function findPopup(
  development: WebsiteData,
  popupId: string,
): PopupData | undefined {
  return development.popups?.find((p) => p.id === popupId);
}

/**
 * Find an HTML content container by ID — searches sections, banners, and popups.
 * Returns `{ content, lastUpdatedAt? }` for any container with HTML content.
 */
function findHTMLContainer(
  development: WebsiteData,
  id: string,
):
  { content: string; source: 'section' | 'popup'; container: any } | undefined {
  const section = findSection(development, id);
  if (section && section.type === 'html') {
    return {
      content: section.content || '',
      source: 'section',
      container: section,
    };
  }
  const popup = findPopup(development, id);
  if (popup) {
    return { content: popup.content || '', source: 'popup', container: popup };
  }
  return undefined;
}

/**
 * Navigate to a nested item using subitems path.
 */
function navigateToItem(
  section: any,
  subitems?: { id: string; propKey: string; type: 'array' }[],
): any | undefined {
  let itemFound = section;
  if (subitems?.length) {
    for (const item of subitems) {
      if (item.type === 'array') {
        itemFound = itemFound?.[item.propKey]?.find(
          (it: any) => it.id === item.id,
        );
      } else {
        throw new Error('unknown subitem type: ' + item.type);
      }
      if (!itemFound) return undefined;
    }
  }
  return itemFound;
}

/**
 * Navigate to an item and replace it with new content.
 * The content is the complete item, so keys removed by the editor
 * (e.g. resetting a CTA color back to default) are dropped as well.
 */
function updateItemAtPath(
  section: any,
  subitems: { id: string; propKey: string; type: 'array' }[] | undefined,
  content: any,
): boolean {
  if (!subitems?.length) return false;

  // Navigate to all but the last subitem
  const parentSubitems = subitems.slice(0, -1);
  const lastSubitem = subitems[subitems.length - 1];

  const parent = navigateToItem(section, parentSubitems);
  if (!parent) return false;

  if (lastSubitem.type !== 'array') {
    throw new Error('unknown subitem type: ' + lastSubitem.type);
  }

  const array = parent[lastSubitem.propKey];
  if (!Array.isArray(array)) return false;

  const itemIndex = array.findIndex((it: any) => it.id === lastSubitem.id);
  if (itemIndex === -1) return false;

  array[itemIndex] = { ...content };
  return true;
}

/**
 * Navigate to a nested property using dot-separated propKey.
 */
function navigateToPropKey(
  item: any,
  propKey: string,
): { parent: any; lastKey: string } | undefined {
  const propKeyParts = propKey.split('.');
  let current = item;

  for (let i = 0; i < propKeyParts.length - 1; i++) {
    const key = propKeyParts[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }

  return {
    parent: current,
    lastKey: propKeyParts[propKeyParts.length - 1],
  };
}

/**
 * Set a value at a nested propKey path.
 */
function setNestedValue(item: any, propKey: string, value: any): void {
  const result = navigateToPropKey(item, propKey);
  if (result) {
    result.parent[result.lastKey] = value;
  }
}

// ============================================================================
// Apply Functions for Text/Content Changes
// ============================================================================

/**
 * Apply a text change to a section.
 * Handles both HTML sections (using DOM manipulation) and structured sections.
 */
export function applyTextChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;
  if (section.type === 'html') return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const itemFound = navigateToItem(section, wu.subitems);
  if (!itemFound) return false;

  setNestedValue(itemFound, wu.propKey, wu.content);

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

export const getElementInnerHtml = (
  development: WebsiteData,
  containerId: string,
  elementPath: string,
) => {
  const found = findHTMLContainer(development, containerId);
  return found
    ? parseHtmlAndFindElement(found.content, elementPath)?.element.innerHTML
    : undefined;
};

/**
 * Apply HTML element changes for structured sections.
 */
export function applyHtmlElementChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  const elementPath = wu.propKey; // elementPath is passed as propKey

  if (!elementPath) {
    console.warn(
      '[WEBSITE_UPDATER] htmlElementChanged: No elementPath provided',
    );
    return false;
  }

  const content = mergeElementEdit(found.content, elementPath, wu);
  if (content === undefined) {
    wu.conflicted = true;
    return false;
  }

  const result = applyHtmlElementChangeToHtml(
    found.content,
    elementPath,
    content,
  );

  if (result.success && result.html) {
    found.container.content = result.html;
    if (found.source === 'section') {
      found.container.lastUpdatedAt = new Date().toISOString();
      found.container.usedSearchParams = extractUsedSearchParamsFromSection(
        found.container,
      );
    }
    console.log('[WEBSITE_UPDATER] htmlElementChanged applied successfully');
    return true;
  } else {
    console.warn('[WEBSITE_UPDATER] htmlElementChanged failed to apply');
    return false;
  }
}

/**
 * Apply HTML variable attribute changes.
 * Updates a variable span's attributes (type, label, param, etc.) inside an HTML section.
 */
export function applyHtmlVariableChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  const elementPath = wu.propKey;
  if (!elementPath) {
    console.warn('[WEBSITE_UPDATER] editHtmlVariable: No elementPath provided');
    return false;
  }

  const variable = wu.content as {
    id: string;
    type: string;
    label: string;
    param?: string;
    dateFormat?: string;
    geoField?: string;
    defaultValue?: string;
    mappings?: { match: string; replace: string; isRegex?: boolean }[];
  };

  const result = parseHtmlAndFindElement(found.content, elementPath);
  if (!result) return false;

  const { doc, element } = result;
  const varSpan = element.querySelector(`[data-sf-var-id="${variable.id}"]`);
  if (!varSpan) return false;

  // Update required attributes
  varSpan.setAttribute('data-sf-var-type', variable.type);
  varSpan.setAttribute('data-sf-var-label', variable.label);
  varSpan.textContent = `{{${variable.label}}}`;

  // Set or remove optional attributes
  const optionalAttrs: [string, string | undefined][] = [
    ['data-sf-var-param', variable.param],
    ['data-sf-var-date-format', variable.dateFormat],
    ['data-sf-var-geo-field', variable.geoField],
    ['data-sf-var-default', variable.defaultValue],
    [
      'data-sf-var-mappings',
      variable.mappings?.length ? JSON.stringify(variable.mappings) : undefined,
    ],
  ];
  for (const [attr, value] of optionalAttrs) {
    if (value) {
      varSpan.setAttribute(attr, value);
    } else {
      varSpan.removeAttribute(attr);
    }
  }

  found.container.content = getHtmlFromDoc(doc);
  if (found.source === 'section') {
    found.container.lastUpdatedAt = new Date().toISOString();
  }
  return true;
}

/**
 * Apply dynamic CMS list settings (sort / limit / filter / freshness mode) to
 * the list element identified by `elementPath`. Mirrors the data attributes
 * parsed by the sites renderer (`data-sf-cms-sort` = `fieldId:asc|desc`,
 * `data-sf-cms-limit` = positive int, `data-sf-cms-filter` = JSON clause array,
 * `data-sf-cms-mode` = `live`, with `snapshot` being the implicit default so
 * the attribute is removed for it).
 */
export function applyCmsListSettingsChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  const elementPath = wu.propKey;
  if (!elementPath) {
    console.warn(
      '[WEBSITE_UPDATER] editCmsListSettings: No elementPath provided',
    );
    return false;
  }

  const settings = wu.content as {
    sort?: { fieldId: string; direction: 'asc' | 'desc' } | null;
    limit?: number | null;
    filter?: CMSFilterClause[] | null;
    mode?: 'snapshot' | 'live';
  };

  const result = parseHtmlAndFindElement(found.content, elementPath);
  if (!result) return false;

  const { doc, element } = result;

  if (settings.sort && settings.sort.fieldId) {
    element.setAttribute(
      'data-sf-cms-sort',
      `${settings.sort.fieldId}:${settings.sort.direction}`,
    );
  } else {
    element.removeAttribute('data-sf-cms-sort');
  }

  if (typeof settings.limit === 'number' && settings.limit > 0) {
    element.setAttribute('data-sf-cms-limit', String(settings.limit));
  } else {
    element.removeAttribute('data-sf-cms-limit');
  }

  if (settings.filter && settings.filter.length) {
    element.setAttribute('data-sf-cms-filter', JSON.stringify(settings.filter));
  } else {
    element.removeAttribute('data-sf-cms-filter');
  }

  if (settings.mode === 'live') {
    element.setAttribute('data-sf-cms-mode', 'live');
  } else {
    element.removeAttribute('data-sf-cms-mode');
  }

  found.container.content = getHtmlFromDoc(doc);
  if (found.source === 'section') {
    found.container.lastUpdatedAt = new Date().toISOString();
  }
  return true;
}

/**
 * Apply the data-freshness mode of an inline single-entry CMS binding. The
 * `elementPath` points at the field element that was inspected; the mode lives
 * on its entry-scope ancestor (the element carrying `data-sf-cms-entry`), so we
 * walk up to it before setting `data-sf-cms-mode` (removed for the `snapshot`
 * default).
 */
export function applyCmsEntryModeChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  const elementPath = wu.propKey;
  if (!elementPath) {
    console.warn('[WEBSITE_UPDATER] editCmsEntryMode: No elementPath provided');
    return false;
  }

  const settings = wu.content as { mode?: 'snapshot' | 'live' };

  const result = parseHtmlAndFindElement(found.content, elementPath);
  if (!result) return false;

  const { doc, element } = result;

  const entryElement = element.closest('[data-sf-cms-entry]');
  if (!entryElement) {
    console.warn(
      '[WEBSITE_UPDATER] editCmsEntryMode: No entry-scope ancestor found',
    );
    return false;
  }

  if (settings.mode === 'live') {
    entryElement.setAttribute('data-sf-cms-mode', 'live');
  } else {
    entryElement.removeAttribute('data-sf-cms-mode');
  }

  found.container.content = getHtmlFromDoc(doc);
  if (found.source === 'section') {
    found.container.lastUpdatedAt = new Date().toISOString();
  }
  return true;
}

/**
 * Apply form replacement.
 */
export function applyReplaceFormChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  const result = parseHtmlAndFindElement(found.content, wu.propKey);
  if (!result) return false;

  const { doc, element } = result;

  if (element.tagName.toLowerCase() !== 'form') {
    console.warn(`[REPLACE_FORM] Target element is not a form: ${wu.propKey}`);
    return false;
  }

  element.setAttribute('data-form-id', wu.content.formId || '');

  found.container.content = getHtmlFromDoc(doc);

  if (found.source === 'section') {
    found.container.lastUpdatedAt = new Date().toISOString();
    found.container.usedSearchParams = extractUsedSearchParamsFromSection(
      found.container,
    );
  }

  console.log('[REPLACE_FORM] Successfully updated form element');
  return true;
}

// ============================================================================
// Apply Functions for Section Movement/Order
// ============================================================================

/**
 * Move a section up or down in the sections array.
 */
export function applyMoveSectionChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
  direction: 'up' | 'down',
): boolean {
  const sectionIdx = development.sections?.findIndex(
    (s: any) => s.id === wu.sectionId,
  );
  if (sectionIdx === -1) return false;

  const section = development.sections[sectionIdx];
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const idxShift = direction === 'up' ? -1 : 1;
  const newIdx = Math.max(0, sectionIdx + idxShift);
  const newSections = [
    ...development.sections.filter((s: any) => s.id !== wu.sectionId),
  ];
  newSections.splice(newIdx, 0, section);
  development.sections = newSections;

  return true;
}

/**
 * Add a section above or below the target section.
 */
export function applyAddSectionChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
  position: 'above' | 'below',
): boolean {
  if (!wu.content) return false;

  const alreadyAddedIdx = development.sections?.findIndex(
    (s: any) => s.id === wu.content.id,
  );

  // If the section is already added, return false
  if (alreadyAddedIdx !== -1) return false;

  let sectionIdx = development.sections?.findIndex(
    (s: any) => s.id === wu.sectionId,
  );
  if (sectionIdx === -1 && position === 'above') {
    sectionIdx = development.sections?.length || 0;
  }

  console.log('[WEBSITE_UPDATER]', wu.type, wu, sectionIdx);

  const newSection = { ...wu.content } as SectionData;

  const idxShift = position === 'above' ? -1 : 1;
  const newIdx = Math.max(0, sectionIdx + idxShift);
  const newSections = [...development.sections];
  newSections.splice(newIdx, 0, newSection);
  development.sections = newSections;

  return true;
}

/**
 * Reorder sections based on provided order.
 */
export function applyReorderSectionsChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  if (!wu.content || !Array.isArray(wu.content)) return false;

  const sectionsOrderMap: Record<string, number> = wu.content.reduce(
    (acc, section, idx) => {
      acc[section.id] = idx;
      return acc;
    },
    {} as Record<string, number>,
  );

  const currentSectionsOrderMap: Record<string, number> =
    development.sections.reduce(
      (acc, section, idx) => {
        acc[section.id] = idx;
        return acc;
      },
      {} as Record<string, number>,
    );

  console.log('[WEBSITE_UPDATER]', wu.type, wu, wu.content);

  const newSections = [...development.sections].sort((a, b) => {
    const aOrder = sectionsOrderMap[a.id] ?? currentSectionsOrderMap[a.id] ?? 0;
    const bOrder = sectionsOrderMap[b.id] ?? currentSectionsOrderMap[b.id] ?? 0;
    return aOrder - bOrder;
  });
  development.sections = newSections;

  return true;
}

/**
 * Delete a section.
 */
export function applyDeleteSectionChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  development.sections = development.sections?.filter(
    (s: any) => s.id !== wu.sectionId,
  );
  development.banners =
    development.banners?.filter((s: any) => s.id !== wu.sectionId) ?? [];

  return true;
}

/**
 * Duplicate a section.
 */
export function applyDuplicateSectionChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const sectionIdx = development.sections?.findIndex(
    (s: any) => s.id === wu.sectionId,
  );
  if (sectionIdx === -1) return false;

  const section = development.sections[sectionIdx];
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const newId = wu.id || uid(8);

  if (!development.sections.find((s) => s.id === newId)) {
    const newSections = [...development.sections];
    newSections.splice(sectionIdx + 1, 0, {
      ...JSON.parse(JSON.stringify(section)),
      id: newId,
    });
    development.sections = newSections;
  }

  return true;
}

// ============================================================================
// Apply Functions for Design Changes
// ============================================================================

/**
 * Apply design/variant change to a section.
 */
export function applyDesignChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  if (wu.content) {
    // apply all section in content to the section
    Object.entries(wu.content).forEach(([key, value]) => {
      const _key: keyof typeof section = key as any;
      if (_key === 'id') return;
      section[_key] = value;
    });
  } else {
    // Switch between variants one by one
    const currentVariant = section.variant;
    const allVariants = sectionTypeToVariantMapping[section.type]?.concat(
      (accountSectionTypeToVariantMapping[wu.accountId]?.[section.type] ??
        []) as any,
    );
    const relevantVariants = allVariants.filter((v) =>
      section.type !== 'hero'
        ? true
        : development.formIds?.length
          ? true
          : v !== 'v6' && v !== 'v7',
    );
    if (!relevantVariants?.length) return false;

    const currentVariantIdx = relevantVariants.indexOf(currentVariant);
    const newVariant =
      relevantVariants[(currentVariantIdx + 1) % relevantVariants.length];

    // const newVariant = getRandomElementWithSeed(wu.id, allVariants);
    section.variant = newVariant as any;
  }

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

// ============================================================================
// Apply Functions for Section Items
// ============================================================================

/**
 * Add a section item to an array.
 */
export function applyAddSectionItemChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const result = navigateToPropKey(section, wu.propKey);
  if (!result) return false;

  const { parent, lastKey } = result;
  const array = parent[lastKey];

  const newId = wu.id || uid(8);
  if (Array.isArray(array)) {
    if (array.some((item: any) => item.id === newId)) return true;
    const itemToCopy = wu.content || array[0];
    const newItem = {
      ...(itemToCopy ? JSON.parse(JSON.stringify(itemToCopy)) : {}),
      id: newId,
    };
    parent[lastKey] = [...array, newItem];
  } else {
    parent[lastKey] = [{ id: newId }];
  }

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

/**
 * Delete a section item from an array.
 */
export function applyDeleteSectionItemChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const result = navigateToPropKey(section, wu.propKey);
  if (!result) return false;

  const { parent, lastKey } = result;
  const array = parent[lastKey];

  if (Array.isArray(array) && array.length) {
    parent[lastKey] = array.filter((item: any) => item.id !== wu.content?.id);
  }

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

/**
 * Edit a section item in an array.
 */
export function applyEditSectionItemChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const result = navigateToPropKey(section, wu.propKey);
  if (!result) return false;

  const { parent, lastKey } = result;
  const array = parent[lastKey];

  if (Array.isArray(array) && array.length) {
    const itemIndex = array.findIndex(
      (item: any) => item.id === wu.content?.id,
    );
    if (itemIndex !== -1) {
      array[itemIndex] = wu.content?.data || array[itemIndex];
    }
  }

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

/**
 * Duplicate a section item in an array.
 */
export function applyDuplicateSectionItemChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const result = navigateToPropKey(section, wu.propKey);
  if (!result) return false;

  const array = result.parent[result.lastKey];
  if (!Array.isArray(array) || !array.length) return true;

  const newId = wu.id || uid(8);
  if (array.some((item: any) => item.id === newId)) return false;

  const itemIndex = array.findIndex((item: any) => item.id === wu.content?.id);
  if (itemIndex !== -1) {
    array.splice(itemIndex + 1, 0, {
      ...array[itemIndex],
      id: newId,
    });
  }

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

// ============================================================================
// Apply Functions for Button/Action Changes
// ============================================================================

/**
 * Edit button properties (for structured sections).
 */
export function applyEditButtonChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  if (!updateItemAtPath(section, wu.subitems, wu.content)) {
    console.warn('[WEBSITE_UPDATER] Failed to update button');
    return false;
  }

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  console.log('[WEBSITE_UPDATER] Button change applied successfully');
  return true;
}

/**
 * Edit HTML action (link, form, stepper navigation) on a button/link element.
 * Handles both HTML sections (using shared utility) and structured sections.
 */
export function applyEditHtmlActionChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  try {
    const result = applyHtmlActionToHtml(
      found.content,
      wu.propKey, // elementPath is passed as propKey
      wu.content,
    );

    if (result.success && result.html) {
      found.container.content = result.html;
      if (found.source === 'section') {
        found.container.lastUpdatedAt = new Date().toISOString();
      }
      return true;
    }

    console.log(
      '[HTML_ACTION_UPDATE] Successfully updated action element in HTML section',
    );
    return true;
  } catch (error) {
    console.error(
      '[HTML_ACTION_UPDATE] Error updating action element in HTML:',
      error,
    );
    return false;
  }
}

// ============================================================================
// Apply Functions for HTML List Operations
// ============================================================================

/**
 * Delete an item from an HTML list.
 * wu.propKey contains the listPath (data-sf-list value)
 * wu.content.itemId is the item to delete
 */
export function applyDeleteHtmlListItemChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  const content = wu.content as { listPath: string; itemId: string };

  const result = applyDeleteHtmlListItem(
    found.content,
    content.listPath,
    content.itemId,
  );

  if (result.success && result.html) {
    found.container.content = result.html;
    if (found.source === 'section') {
      found.container.lastUpdatedAt = new Date().toISOString();
    }
    console.log('[WEBSITE_UPDATER] deleteHtmlListItem applied successfully');
    return true;
  } else {
    console.warn(
      '[WEBSITE_UPDATER] deleteHtmlListItem failed:',
      result.error || 'Unknown error',
    );
    return false;
  }
}

/**
 * Duplicate an item in an HTML list.
 * wu.propKey contains the listPath (data-sf-list value)
 * wu.content.itemId is the item to duplicate
 */
export function applyDuplicateHtmlListItemChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  const content = wu.content as {
    listPath: string;
    itemId: string;
    newItemId: string;
  };

  const result = applyDuplicateHtmlListItem(
    found.content,
    content.listPath,
    content.itemId,
    content.newItemId,
  );

  if (result.success && result.html) {
    found.container.content = result.html;
    if (found.source === 'section') {
      found.container.lastUpdatedAt = new Date().toISOString();
    }
    console.log('[WEBSITE_UPDATER] duplicateHtmlListItem applied successfully');
    return true;
  } else {
    console.warn(
      '[WEBSITE_UPDATER] duplicateHtmlListItem failed:',
      result.error || 'Unknown error',
    );
    return false;
  }
}

/**
 * Move an item in an HTML list (up or down).
 * wu.propKey contains the listPath (data-sf-list value)
 * wu.content.itemId is the item to move
 * wu.content.direction is 'up' | 'down'
 */
export function applyMoveHtmlListItemChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  const content = wu.content as {
    listPath: string;
    itemId: string;
    direction: 'up' | 'down';
  };

  const result = applyMoveHtmlListItem(
    found.content,
    content.listPath,
    content.itemId,
    content.direction,
  );

  if (result.success && result.html) {
    found.container.content = result.html;
    if (found.source === 'section') {
      found.container.lastUpdatedAt = new Date().toISOString();
    }
    console.log('[WEBSITE_UPDATER] moveHtmlListItem applied successfully');
    return true;
  } else {
    console.warn(
      '[WEBSITE_UPDATER] moveHtmlListItem failed:',
      result.error || 'Unknown error',
    );
    return false;
  }
}

/**
 * Toggle hidden state of an item in an HTML list.
 * Hidden items are removed only in non-preview render mode.
 */
export function applyToggleHtmlListItemHiddenChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  const content = wu.content as {
    listPath: string;
    itemId: string;
    isHidden: boolean;
  };

  const result = applyToggleHtmlListItemHidden(
    found.content,
    content.listPath,
    content.itemId,
    content.isHidden,
  );

  if (result.success && result.html) {
    found.container.content = result.html;
    if (found.source === 'section') {
      found.container.lastUpdatedAt = new Date().toISOString();
    }
    console.log(
      '[WEBSITE_UPDATER] toggleHtmlListItemHidden applied successfully',
    );
    return true;
  } else {
    console.warn(
      '[WEBSITE_UPDATER] toggleHtmlListItemHidden failed:',
      result.error || 'Unknown error',
    );
    return false;
  }
}

// ============================================================================
// Apply Functions for Config/Brand Changes
// ============================================================================

/**
 * Edit config or brand settings.
 */
export function applyEditConfigChange(
  configKey: 'config' | 'brand',
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  console.log('[WEBSITE_UPDATER]', wu.type, configKey, wu);

  if (!development[configKey]) {
    development[configKey] = {};
  }

  development[configKey] = {
    ...development[configKey],
    ...wu.content,
  };

  development.sections?.forEach((section: any) => {
    section.lastUpdatedAt = new Date().toISOString();
  });

  return true;
}

/**
 * Reset config or brand settings.
 */
export function applyResetConfigChange(
  configKey: 'config' | 'brand',
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  console.log('[WEBSITE_UPDATER]', wu.type, configKey, wu);

  development[configKey] = wu.content;

  development.sections?.forEach((section: any) => {
    section.lastUpdatedAt = new Date().toISOString();
  });

  return true;
}

// ============================================================================
// Apply Functions for Image Changes
// ============================================================================

/**
 * Select/change an image in a section.
 * Handles both HTML sections (using shared utility) and structured sections.
 */
export function applySelectImageChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  // First try to find as a popup (always HTML content)
  const popup = findPopup(development, wu.sectionId);
  if (popup) {
    const result = applySelectAssetToHtml(
      popup.content || '',
      wu.propKey,
      wu.content,
    );
    if (result.success && result.html) {
      popup.content = result.html;
      return true;
    }
    return false;
  }

  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  // Handle HTML sections using shared utility
  if (section.type === 'html') {
    // Use new applySelectAssetToHtml for img/video/svg switching support
    const result = applySelectAssetToHtml(
      section.content || '',
      wu.propKey, // elementPath is passed as propKey
      wu.content,
    );

    if (result.success && result.html) {
      section.content = result.html;
      section.lastUpdatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  // Handle structured sections (non-HTML) - original logic
  let itemFound: any = section;
  if (wu.subitems?.length) {
    wu.subitems.forEach((item) => {
      if (item.type === 'array') {
        itemFound = itemFound?.[item.propKey]?.find(
          (it: any) => it.id === item.id,
        );
      } else {
        throw new Error('unknown subitem type: ' + item.type);
      }
    });
    if (!itemFound) return false;
  }
  const propKeyParts = wu.propKey.split('.');
  propKeyParts.forEach((propKeyPart, i) => {
    const isLast = i + 1 === propKeyParts.length;
    if (isLast) {
      itemFound[propKeyPart] = mergeImages(wu.content, itemFound[propKeyPart]);
    } else {
      let newItem = itemFound[propKeyPart];
      if (!newItem) newItem = itemFound[propKeyPart] = {};
      itemFound = newItem;
    }
  });

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

/**
 * Apply thumbnail selection to a video element.
 * Sets data-thumbnail-url, data-thumbnail-alt, and poster attributes.
 */
export function applySelectThumbnailChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const found = findHTMLContainer(development, wu.sectionId);
  if (!found) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, found.container);

  const result = applySelectThumbnailToHtml(
    found.content,
    wu.propKey, // elementPath is passed as propKey
    wu.content,
  );

  if (result.success && result.html) {
    found.container.content = result.html;
    if (found.source === 'section') {
      found.container.lastUpdatedAt = new Date().toISOString();
    }
    return true;
  }

  console.warn('[WEBSITE_UPDATER] applySelectThumbnailToHtml failed', result);
  return false;
}

/**
 * Edit section properties directly.
 */
export function applyEditSection(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);

  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  const contentUpdate = wu.content as Record<string, unknown>;
  Object.assign(section, contentUpdate);
  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

/**
 * Edit image settings (objectFit, objectPosition, hideOnMobile).
 * Handles both HTML sections (using shared utility) and structured sections.
 */
export function applyEditImageSettings(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  // First try to find as a popup (always HTML content)
  const popup = findPopup(development, wu.sectionId);
  if (popup) {
    const settingType = wu.content.type;
    if (settingType === 'hideOnMobile') {
      return applyHideOnMobileToHtmlSection(popup, wu.propKey, wu.content);
    }
    const result = applyAssetSettingsToHtml(popup.content || '', wu.propKey, {
      type: settingType,
      value: wu.content.value,
    });
    if (result.success && result.html) {
      popup.content = result.html;
      return true;
    }
    return false;
  }

  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  // Handle HTML sections using shared utility
  if (section.type === 'html') {
    const settingType = wu.content.type;

    // hideOnMobile is handled differently - uses classes instead of styles
    if (settingType === 'hideOnMobile') {
      return applyHideOnMobileToHtmlSection(section, wu.propKey, wu.content);
    }

    // Use new applyAssetSettingsToHtml for img/video setting support
    const result = applyAssetSettingsToHtml(section.content || '', wu.propKey, {
      type: settingType,
      value: wu.content.value,
    });

    if (result.success && result.html) {
      section.content = result.html;
      section.lastUpdatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  // Handle structured sections (non-HTML) - original logic
  let itemFound: any = section;
  if (wu.subitems?.length) {
    wu.subitems.forEach((item) => {
      if (item.type === 'array') {
        itemFound = itemFound?.[item.propKey]?.find(
          (it: any) => it.id === item.id,
        );
      } else {
        throw new Error('unknown subitem type: ' + item.type);
      }
    });
    if (!itemFound) return false;
  }
  const propKeyParts = wu.propKey.split('.');
  propKeyParts.forEach((propKeyPart, i) => {
    const isLast = i + 1 === propKeyParts.length;
    if (isLast) {
      itemFound[propKeyPart] = {
        ...itemFound[propKeyPart],
        [wu.content.type]: wu.content.value,
      };
    } else {
      let newItem = itemFound[propKeyPart];
      if (!newItem) newItem = itemFound[propKeyPart] = {};
      itemFound = { ...newItem, [wu.content.type]: wu.content.value };
    }
  });

  section.lastUpdatedAt = new Date().toISOString();
  section.usedSearchParams = extractUsedSearchParamsFromSection(section);

  return true;
}

/**
 * Helper for hideOnMobile setting in HTML sections.
 */
function applyHideOnMobileToHtmlSection(
  section: any,
  elementPath: string,
  content: { value: boolean },
): boolean {
  try {
    const doc = parseHtmlFragment(section.content || '');
    const element = doc.querySelector(elementPath);

    if (!element) return false;

    const existingClasses = element.getAttribute('class') || '';
    const classArray = existingClasses.split(' ').filter((cls) => cls.trim());

    if (content.value) {
      // Add hidden on mobile classes
      if (!classArray.includes('hidden')) {
        classArray.push('hidden', 'md:block');
      }
    } else {
      // Remove hidden on mobile classes
      const filteredClasses = classArray.filter(
        (cls) => cls !== 'hidden' && cls !== 'md:block',
      );
      element.setAttribute('class', filteredClasses.join(' ').trim());
      section.content = getHtmlFromDoc(doc);
      section.lastUpdatedAt = new Date().toISOString();
      return true;
    }

    element.setAttribute('class', classArray.join(' ').trim());
    section.content = getHtmlFromDoc(doc);
    section.lastUpdatedAt = new Date().toISOString();
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// Apply Functions for Global Section Conversion
// ============================================================================

/**
 * Convert global section to local.
 */
export function applyConvertGlobalToLocalChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  section.global = null;
  section.lastUpdatedAt = new Date().toISOString();

  return true;
}

/**
 * Convert local section to global.
 */
export function applyConvertLocalToGlobalChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const section = findSection(development, wu.sectionId);
  if (!section) return false;

  console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

  section.global = wu.content.global;
  section.lastUpdatedAt = new Date().toISOString();

  return true;
}

// ============================================================================
// Apply Functions for Revert/Discard
// ============================================================================

/**
 * Revert to a previous state.
 */
export function applyRevertChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const after = (wu.content as { after?: WebsiteData } | undefined)?.after;
  if (!wu.before || !after) return false;

  revertTouched(development, wu.before, after, ['sections', 'popups']);
  return true;
}

/**
 * Discard changes and restore from content.
 */
export function applyDiscardChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  development.sections = wu.content?.sections || development.sections;
  return true;
}

/**
 * Apply one accepted agent change, carried as its own `SessionChangeContent`.
 * The stored paths are rooted at the entity document (`development.sections`),
 * so the fold runs against a `{ development }` wrapper and the result is copied
 * back in place — `applyChange` mutates, and removed sections/popups must drop
 * out of the caller's object.
 */
export function applyAgentChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  const content = wu.content as SessionChangeContent | undefined;
  if (!content) return false;
  const conflicts = new MergeConflicts();

  // The global section editor holds its section as `sections[0]` of a website
  // wrapper, while the change is rooted at the global section document.
  if (wu.websiteId.startsWith('globalSectionId_')) {
    const foldedSection = applySessionChangeContent(
      { development: development.sections[0] },
      content,
      conflicts,
    ) as { development?: SectionData };
    wu.conflicted = conflicts.count > 0;
    if (!foldedSection.development) return false;
    development.sections = [
      JSON.parse(JSON.stringify(foldedSection.development)) as SectionData,
    ];
    return true;
  }

  const folded = applySessionChangeContent(
    { development },
    content,
    conflicts,
  ) as {
    development?: WebsiteData;
  };
  wu.conflicted = conflicts.count > 0;
  if (!folded.development) return false;

  // Clone before clearing: the fold can hand back the very object we are about
  // to empty, and clearing the target first would wipe the source with it.
  const next = JSON.parse(JSON.stringify(folded.development)) as WebsiteData;
  const target = development as unknown as Record<string, unknown>;
  for (const key of Object.keys(target)) {
    delete target[key];
  }
  Object.assign(development, next);
  return true;
}

// ============================================================================
// Apply Functions for Popups
// ============================================================================

/**
 * Add a new popup.
 * content should be the PopupData object to add
 */
export function applyAddPopupChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  if (!wu.content) return false;

  if (!development.popups) {
    development.popups = [];
  }

  development.popups.push(wu.content);
  return true;
}

/**
 * Update an existing popup.
 * sectionId should be the popup ID
 * content should be the partial PopupData with updates
 */
export function applyUpdatePopupChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  if (!development.popups) return false;

  const popupIndex = development.popups.findIndex((p) => p.id === wu.sectionId);
  if (popupIndex === -1) return false;

  // With the popup the panel started from, only the fields it changed are
  // applied, so text edited in the canvas or by another tab since is kept.
  development.popups[popupIndex] = wu.basePopup
    ? (mergeValue(
        development.popups[popupIndex],
        wu.basePopup,
        wu.content,
      ) as PopupData)
    : { ...development.popups[popupIndex], ...wu.content };
  return true;
}

/**
 * Delete a popup.
 * sectionId should be the popup ID
 */
export function applyDeletePopupChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  if (!development.popups) return false;

  const popupIndex = development.popups.findIndex((p) => p.id === wu.sectionId);
  if (popupIndex === -1) return false;

  development.popups.splice(popupIndex, 1);
  return true;
}

// ============================================================================
// Main Apply Function
// ============================================================================

/**
 * Apply a single WebsiteUpdate to WebsiteData.
 */
export function applyChange(
  wu: WebsiteUpdate,
  development: WebsiteData,
): boolean {
  switch (wu.type) {
    case 'text':
      return applyTextChange(wu, development);

    case 'htmlElementChanged':
      return applyHtmlElementChange(wu, development);

    case 'moveSectionUp':
      return applyMoveSectionChange(wu, development, 'up');

    case 'moveSectionDown':
      return applyMoveSectionChange(wu, development, 'down');

    case 'addSectionAbove':
      return applyAddSectionChange(wu, development, 'above');

    case 'addSectionBelow':
      return applyAddSectionChange(wu, development, 'below');

    case 'reorderSections':
      return applyReorderSectionsChange(wu, development);

    case 'deleteSection':
      return applyDeleteSectionChange(wu, development);

    case 'duplicateSection':
      return applyDuplicateSectionChange(wu, development);

    case 'addSectionItem':
      return applyAddSectionItemChange(wu, development);

    case 'deleteSectionItem':
      return applyDeleteSectionItemChange(wu, development);

    case 'editSectionItem':
      return applyEditSectionItemChange(wu, development);

    case 'editButton':
      return applyEditButtonChange(wu, development);

    case 'editHtmlAction':
      return applyEditHtmlActionChange(wu, development);

    case 'editHtmlVariable':
      return applyHtmlVariableChange(wu, development);

    case 'editCmsListSettings':
      return applyCmsListSettingsChange(wu, development);

    case 'editCmsEntryMode':
      return applyCmsEntryModeChange(wu, development);

    case 'editConfig':
      return applyEditConfigChange('config', wu, development);

    case 'editBrand':
      return applyEditConfigChange('brand', wu, development);

    case 'resetConfig':
      return applyResetConfigChange('config', wu, development);

    case 'resetBrand':
      return applyResetConfigChange('brand', wu, development);

    case 'duplicateSectionItem':
      return applyDuplicateSectionItemChange(wu, development);

    case 'selectImage':
      return applySelectImageChange(wu, development);

    case 'selectThumbnail':
      return applySelectThumbnailChange(wu, development);

    case 'changeDesign':
      return applyDesignChange(wu, development);

    case 'editSection':
      return applyEditSection(wu, development);

    case 'editImageSettings':
      return applyEditImageSettings(wu, development);

    case 'revertChange':
      return applyRevertChange(wu, development);

    case 'discard':
      return applyDiscardChange(wu, development);

    case 'restoreSnapshot':
      return applyAgentChange(wu, development);

    case 'agentChange':
      return applyAgentChange(wu, development);

    case 'convertGlobalToLocal':
      return applyConvertGlobalToLocalChange(wu, development);

    case 'convertLocalToGlobal':
      return applyConvertLocalToGlobalChange(wu, development);

    case 'replaceForm':
      return applyReplaceFormChange(wu, development);

    case 'addPopup':
      return applyAddPopupChange(wu, development);

    case 'updatePopup':
      return applyUpdatePopupChange(wu, development);

    case 'deletePopup':
      return applyDeletePopupChange(wu, development);

    case 'deleteHtmlListItem':
      return applyDeleteHtmlListItemChange(wu, development);

    case 'duplicateHtmlListItem':
      return applyDuplicateHtmlListItemChange(wu, development);

    case 'moveHtmlListItem':
      return applyMoveHtmlListItemChange(wu, development);

    case 'toggleHtmlListItemHidden':
      return applyToggleHtmlListItemHiddenChange(wu, development);

    default:
      throw new Error('Unable to apply update of type: ' + wu.type);
  }
}
